import { useState } from "react";
import { Link } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { api, apiError } from "@/lib/api";
import { Instagram, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function Footer() {
  const { settings } = useSettings();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const brand = settings.brand_name || "Kys Boutique";
  const social = settings.social || {};

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email, hp: "" });
      toast.success("You're on the list!");
      setEmail("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const policies = [
    { label: "Privacy Policy", to: "/policy/privacy" },
    { label: "Terms & Conditions", to: "/policy/terms" },
    { label: "Shipping Policy", to: "/policy/shipping" },
    { label: "Returns & Exchanges", to: "/policy/returns" },
    { label: "Custom Order Policy", to: "/policy/custom_order" },
    { label: "Cancellation Policy", to: "/policy/cancellation" },
  ];

  return (
    <footer className="mt-24 border-t border-kys-blush-border bg-kys-ink text-white">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-4">
          <div className="md:col-span-1">
            <h3 className="font-serif text-3xl text-white">{brand}</h3>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-white/60">
              {settings.tagline || "Handcrafted performance wear & custom stage couture."}
            </p>
            {(social.instagram || settings.homepage?.instagram_url) && (
              <a href={social.instagram || settings.homepage?.instagram_url} target="_blank" rel="noreferrer"
                className="mt-5 inline-flex items-center gap-2 text-sm text-white/70 hover:text-kys-accent">
                <Instagram className="h-4 w-4" /> Follow us
              </a>
            )}
          </div>

          <div>
            <p className="label-accent mb-4 text-kys-accent">Shop</p>
            <ul className="space-y-2.5 text-sm text-white/70">
              <li><Link to="/shop" className="hover:text-white">Ready to Ship</Link></li>
              <li><Link to="/builder" className="hover:text-white">Build Your Set</Link></li>
            </ul>
          </div>

          <div>
            <p className="label-accent mb-4 text-kys-accent">Help</p>
            <ul className="space-y-2.5 text-sm text-white/70">
              <li><Link to="/about" className="hover:text-white">About</Link></li>
              <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
              {policies.map((p) => (
                <li key={p.to}><Link to={p.to} className="hover:text-white">{p.label}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <p className="label-accent mb-4 text-kys-accent">Newsletter</p>
            <p className="mb-3 text-sm text-white/70">Be first to see new drops.</p>
            <form onSubmit={subscribe} className="flex gap-2">
              <input data-testid="newsletter-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="w-full rounded border border-white/20 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-kys-accent focus:outline-none" />
              <button data-testid="newsletter-submit" disabled={busy}
                className="flex items-center rounded bg-kys-accent px-4 py-2 text-sm font-semibold text-white transition hover:bg-kys-accent-dark disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join"}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 text-xs text-white/40 sm:flex-row">
          <p>© {new Date().getFullYear()} {settings.legal_name || "Kys Boutique LLC"}. All rights reserved.</p>
          <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-white/70">Owner Login</Link>
        </div>
      </div>
    </footer>
  );
}
